import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { GripVertical } from 'lucide-react';

interface ListaReordenableProps<T extends { id: string }> {
  items: T[];
  /** Recibe los ids en el orden nuevo, solo cuando el orden cambió. */
  onReordenar: (ids: string[]) => void;
  /** Texto que identifica al ítem en la manija ("Mover <etiqueta>"). */
  etiquetaItem: (item: T) => string;
  /** Deja las manijas visibles pero sin poder mover nada. */
  deshabilitado?: boolean;
  /** `ul` hace que cada fila sea un `li`. */
  as?: 'div' | 'ul';
  className?: string;
  /** Renderiza la fila; `asa` es la manija para arrastrar y va donde la fila decida. */
  children: (item: T, asa: React.ReactNode) => React.ReactNode;
}

const mismoOrden = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, indice) => id === b[indice]);

/**
 * Lista que se reordena arrastrando una manija (o con ↑/↓ con el foco en ella).
 *
 * El orden lo define `items`; la lista solo guarda uno propio mientras se arrastra
 * y hasta que `items` cambia de orden (por ejemplo, con la actualización optimista
 * de quien la usa), para que no vuelva a la posición anterior mientras se guarda.
 */
export function ListaReordenable<T extends { id: string }>({
  items,
  onReordenar,
  etiquetaItem,
  deshabilitado = false,
  as = 'div',
  className,
  children,
}: ListaReordenableProps<T>) {
  const idsActuales = items.map((item) => item.id);
  const clave = idsActuales.join('\u0000');
  const [idsLocales, setIdsLocales] = useState<string[] | null>(null);
  const arrastrandoRef = useRef(false);
  // Orden durante el arrastre: el fin del arrastre no depende de cuándo se renderizó.
  const ordenArrastreRef = useRef<string[]>([]);
  const manijasRef = useRef(new Map<string, HTMLButtonElement>());
  const enfocarRef = useRef<string | null>(null);

  useEffect(() => {
    if (!arrastrandoRef.current) setIdsLocales(null);
  }, [clave]);

  // Mover la fila en el DOM le saca el foco: se devuelve a su manija.
  useLayoutEffect(() => {
    if (!enfocarRef.current) return;
    manijasRef.current.get(enfocarRef.current)?.focus();
    enfocarRef.current = null;
  });

  const porId = new Map(items.map((item) => [item.id, item] as const));
  const ids = idsLocales
    ? [
      ...idsLocales.filter((id) => porId.has(id)),
      ...idsActuales.filter((id) => !idsLocales.includes(id)),
    ]
    : idsActuales;

  const confirmar = (nuevos: string[]) => {
    if (mismoOrden(nuevos, idsActuales)) {
      setIdsLocales(null);
      return;
    }

    setIdsLocales(nuevos);
    onReordenar(nuevos);
  };

  const moverConTeclado = (id: string, delta: number) => {
    const desde = ids.indexOf(id);
    const hasta = desde + delta;
    if (deshabilitado || desde < 0 || hasta < 0 || hasta >= ids.length) return;

    const nuevos = [...ids];
    nuevos.splice(desde, 1);
    nuevos.splice(hasta, 0, id);
    enfocarRef.current = id;
    confirmar(nuevos);
  };

  return (
    <Reorder.Group
      as={as}
      axis="y"
      values={ids}
      onReorder={(nuevos: string[]) => {
        ordenArrastreRef.current = nuevos;
        setIdsLocales(nuevos);
      }}
      className={className}
    >
      {ids.map((id) => {
        const item = porId.get(id);
        if (!item) return null;

        return (
          <FilaReordenable
            key={id}
            id={id}
            as={as === 'ul' ? 'li' : 'div'}
            etiqueta={etiquetaItem(item)}
            deshabilitado={deshabilitado}
            registrarManija={(boton) => {
              if (boton) manijasRef.current.set(id, boton);
              else manijasRef.current.delete(id);
            }}
            onInicioArrastre={() => {
              arrastrandoRef.current = true;
              ordenArrastreRef.current = ids;
              setIdsLocales(ids);
            }}
            onFinArrastre={() => {
              arrastrandoRef.current = false;
              confirmar(ordenArrastreRef.current);
            }}
            onTecla={(event) => {
              if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
              event.preventDefault();
              moverConTeclado(id, event.key === 'ArrowUp' ? -1 : 1);
            }}
          >
            {(asa) => children(item, asa)}
          </FilaReordenable>
        );
      })}
    </Reorder.Group>
  );
}

interface FilaReordenableProps {
  id: string;
  as: 'div' | 'li';
  etiqueta: string;
  deshabilitado: boolean;
  registrarManija: (boton: HTMLButtonElement | null) => void;
  onInicioArrastre: () => void;
  onFinArrastre: () => void;
  onTecla: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  children: (asa: React.ReactNode) => React.ReactNode;
}

const FilaReordenable: React.FC<FilaReordenableProps> = ({
  id,
  as,
  etiqueta,
  deshabilitado,
  registrarManija,
  onInicioArrastre,
  onFinArrastre,
  onTecla,
  children,
}) => {
  // Solo se arrastra desde la manija: el resto de la fila sigue siendo editable.
  const controles = useDragControls();

  const asa = (
    <button
      type="button"
      ref={registrarManija}
      aria-label={`Mover ${etiqueta}`}
      title="Arrastrá para reordenar"
      disabled={deshabilitado}
      onPointerDown={(event) => {
        if (!deshabilitado) controles.start(event);
      }}
      onKeyDown={onTecla}
      style={{ touchAction: 'none' }}
      className="flex h-8 w-6 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );

  return (
    <Reorder.Item
      as={as}
      value={id}
      dragListener={false}
      dragControls={controles}
      onDragStart={onInicioArrastre}
      onDragEnd={onFinArrastre}
      whileDrag={{ scale: 1.01, boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)' }}
      className="relative rounded-lg"
    >
      {children(asa)}
    </Reorder.Item>
  );
};
